// Builds the custom mouse cursor shown over the VNC screen: a small translucent
// white dot, wrapped in a white ring, wrapped in a thin dark outline — so it
// stays visible over both light and dark phone UIs.
//
// noVNC wants the cursor as a raw RGBA pixel buffer, so we render the circle by
// hand. `coverage()` gives each pixel a soft (anti-aliased) 0..1 membership in a
// disc of a given radius, and we composite the three bands using that coverage.
export default function buildDotCursor() {
  const size = 30;
  const center = (size - 1) / 2;

  const outlineRadius = 14;                       // outer edge of the dark outline
  const ringRadius = outlineRadius - 1;           // outer edge of the white ring
  const dotRadius = ringRadius - 2;               // edge of the translucent dot
  const feather = 1;                              // anti-aliasing softness, in px

  const dotAlpha = 0.4;
  const white = [255, 255, 255];
  const black = [0, 0, 0];

  const coverage = (distance, radius) =>
    Math.min(1, Math.max(0, (radius - distance) / feather + 0.5));

  const pixels = new Uint8Array(size * size * 4);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const distance = Math.hypot(x - center, y - center);

      // Each band is the ring between two radii (except the innermost dot).
      const dot = coverage(distance, dotRadius) * dotAlpha;
      const ring = Math.max(0, coverage(distance, ringRadius) - coverage(distance, dotRadius));
      const outline = Math.max(0, coverage(distance, outlineRadius) - coverage(distance, ringRadius));

      const alpha = dot + ring + outline;
      if (alpha <= 0) continue;

      // Blend the band colors, weighted by how much of this pixel each covers.
      const i = (y * size + x) * 4;
      pixels[i] = Math.round((white[0] * dot + white[0] * ring + black[0] * outline) / alpha);
      pixels[i + 1] = Math.round((white[1] * dot + white[1] * ring + black[1] * outline) / alpha);
      pixels[i + 2] = Math.round((white[2] * dot + white[2] * ring + black[2] * outline) / alpha);
      pixels[i + 3] = Math.round(Math.min(1, alpha) * 255);
    }
  }

  return { rgbaPixels: pixels, w: size, h: size, hotx: Math.round(center), hoty: Math.round(center) };
}
